/**
 * CableDragController - Cable dragging and routing
 * 
 * Handles all cable drag operations:
 * - Single cable drag from output/insert ports
 * - Multi-cable drag (batch port patching)
 * - Temp cable preview rendering
 * - Port hit detection and highlighting
 */

import { showToast } from './ToastController.js';
import { getMultiSelectState, clearMultiSelection } from './SelectionController.js';
import { canConnectTypes, canConnectConnectors, canInternallyRoute } from '../services/ConnectionService.js';
import { CONNECTOR_TYPES } from '../constants/index.js';

// ============= STATE =============

// Cable drag state
let cableDragState = null;

// Mouse position tracking for Alt key refresh
let lastMousePosition = null;

// Cached target port info for Alt key refresh
// { deviceId, groupIndex, type, index } or null for free mode
let lastHoveredTargetPortInfo = null;

// Module dependencies (injected via init)
let cablesSvg = null;
let instanceModel = null;
let connectionModel = null;
let screenToCanvasFunc = null;
let getPortPositionFunc = null;
let createCablePathFunc = null;
let getTemplateForDeviceFunc = null;
let createConnectionFunc = null;
let getConnectionSourceGroupFunc = null;
let getConnectionTargetGroupFunc = null;
let getPointerPosFunc = null;

// ============= INIT =============

/**
 * Initialize the cable drag controller with dependencies.
 * 
 * @param {Object} deps - Dependencies
 * @param {SVGElement} deps.cablesSvg - SVG element for rendering cables
 * @param {Object} deps.instanceModel - Device instance model
 * @param {Object} deps.connectionModel - Connection model
 * @param {Function} deps.screenToCanvas - Convert screen coords to canvas coords
 * @param {Function} deps.getPortPosition - Get port position on canvas
 * @param {Function} deps.createCablePath - Generate SVG path string for cable
 * @param {Function} deps.getTemplateForDevice - Get template definition for device
 * @param {Function} deps.createConnection - Create a new connection
 * @param {Function} deps.getConnectionSourceGroup - Get source group for connection
 * @param {Function} deps.getConnectionTargetGroup - Get target group for connection
 * @param {Function} deps.getPointerPos - Get pointer position from event
 */
export function initCableDragController(deps) {
    cablesSvg = deps.cablesSvg;
    instanceModel = deps.instanceModel;
    connectionModel = deps.connectionModel;
    screenToCanvasFunc = deps.screenToCanvas;
    getPortPositionFunc = deps.getPortPosition;
    createCablePathFunc = deps.createCablePath;
    getTemplateForDeviceFunc = deps.getTemplateForDevice;
    createConnectionFunc = deps.createConnection;
    getConnectionSourceGroupFunc = deps.getConnectionSourceGroup;
    getConnectionTargetGroupFunc = deps.getConnectionTargetGroup;
    getPointerPosFunc = deps.getPointerPos;
}

// ============= STATE ACCESSORS =============

/**
 * Get current cable drag state
 */
export function getCableDragState() {
    return cableDragState;
}

/**
 * Check if currently dragging a cable
 */
export function isDraggingCable() {
    return cableDragState !== null;
}

/**
 * Get last mouse position (for Alt key refresh)
 */
export function getLastMousePosition() {
    return lastMousePosition;
}

/**
 * Set last mouse position (for Alt key refresh)
 */
export function setLastMousePosition(pos) {
    lastMousePosition = pos;
}

/**
 * Get last hovered target port info (for Alt key refresh)
 */
export function getLastHoveredTargetPortInfo() {
    return lastHoveredTargetPortInfo;
}

// ============= PORT HIGHLIGHTING =============

/**
 * Highlight valid/invalid target ports during cable drag
 * For internal routing (sourceDirection='input'), only output ports on the SAME device are valid
 */
function highlightTargetPorts(sourceType, sourceDirection, sourceConnector, sourceEl) {
    const isInternalRouting = sourceDirection === 'input';
    const sourceDeviceId = sourceEl?.dataset?.device;
    
    // For internal routing, target output ports on same device
    // For normal routing, target input/insert ports on any device
    const targetSelector = isInternalRouting
        ? '.port[data-direction="output"]'
        : '.port[data-direction="input"], .port[data-direction="insert"]';
    
    document.querySelectorAll(targetSelector).forEach(p => {
        // Don't highlight the same port we're dragging from
        if (p === sourceEl) return;
        
        // For internal routing, only same device is valid
        if (isInternalRouting && p.dataset.device !== sourceDeviceId) {
            p.classList.add('invalid-target');
            return;
        }
        
        const targetTypes = p.dataset.types ? p.dataset.types.split(',') : p.dataset.type;
        const connectorOptions = {
            sourceConnector: sourceConnector,
            targetConnector: p.dataset.connector || null
        };
        
        // For internal routing, use more permissive type matching
        // (any MIDI ↔ any MIDI, any audio ↔ any audio)
        if (isInternalRouting) {
            if (canInternallyRoute(sourceType, targetTypes)) {
                p.classList.add('valid-target');
            } else {
                p.classList.add('invalid-target');
            }
        } else {
            if (canConnectTypes(sourceType, targetTypes, sourceDirection, p.dataset.direction, connectorOptions)) {
                p.classList.add('valid-target');
            } else {
                p.classList.add('invalid-target');
            }
        }
    });
}

/**
 * Clear all port highlighting
 */
function clearPortHighlighting() {
    document.querySelectorAll('.port').forEach(p => {
        p.classList.remove('dragging-source', 'valid-target', 'invalid-target');
    });
}

// ============= CABLE DRAG FUNCTIONS =============

/**
 * Start multi-cable drag (batch port patching)
 */
export function startMultiCableDrag(e, portEl) {
    if (e.type === 'mousedown' && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    
    const multiSelectState = getMultiSelectState();
    
    // Sort selected ports by index for consistent routing order
    const sortedPorts = [...multiSelectState.selectedPorts].sort((a, b) => a.index - b.index);
    
    // Use the first port as the "anchor" for the temp cable
    const firstPort = sortedPorts[0];
    const sourceDirection = portEl.dataset.direction || 'output';
    
    // For internal routing (input->output on same device), set flag for findNearestInputPort
    const isInternalRouting = sourceDirection === 'input';
    
    cableDragState = {
        sourceDeviceId: firstPort.deviceId,
        sourceGroupIndex: firstPort.groupIndex,
        sourceType: firstPort.type,
        sourceIndex: firstPort.index,
        sourceEl: portEl,
        sourceDirection: sourceDirection,
        isMultiDrag: true,
        multiPorts: sortedPorts,
        isInternalRouting: isInternalRouting
    };
    
    // Initialize mouse position from the event for Alt key refresh
    const pos = getPointerPosFunc(e);
    lastMousePosition = { clientX: pos.clientX, clientY: pos.clientY };
    lastHoveredTargetPortInfo = null;

    portEl.classList.add('dragging-source');
    
    // Highlight valid targets
    const sourceConnector = portEl.dataset.connector || null;
    highlightTargetPorts(firstPort.type, sourceDirection, sourceConnector, portEl);

    // Create temp cable from first selected port
    const tempCable = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    tempCable.id = 'temp-cable';
    tempCable.className.baseVal = 'cable-temp';
    
    // For internal routing, append to device's internal SVG layer so it renders on top
    if (isInternalRouting) {
        const deviceEl = document.getElementById(firstPort.deviceId);
        const internalSvg = deviceEl?.querySelector('.device-internal-cables');
        if (internalSvg) {
            internalSvg.appendChild(tempCable);
        } else {
            cablesSvg.appendChild(tempCable);
        }
    } else {
        cablesSvg.appendChild(tempCable);
    }
}

/**
 * Start single cable drag
 */
export function startCableDrag(e, portEl) {
    if (e.type === 'mousedown' && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();

    const direction = portEl.dataset.direction;
    
    // Can start cable drag from output, insert, or input (for internal routing) ports
    // Input ports are only allowed here if app.js validated they're on a router device
    if (direction !== 'output' && direction !== 'insert' && direction !== 'input') return;

    const deviceId = portEl.dataset.device;
    const groupIndex = parseInt(portEl.dataset.group);
    const type = portEl.dataset.type;
    const index = parseInt(portEl.dataset.index);

    // For internal routing (input->output on same device), mark this as internal routing
    const isInternalRouting = direction === 'input';

    cableDragState = {
        sourceDeviceId: deviceId,
        sourceGroupIndex: groupIndex,
        sourceType: type,
        sourceIndex: index,
        sourceEl: portEl,
        sourceDirection: direction,
        sourceConnector: portEl.dataset.connector || null,
        sourceTypes: portEl.dataset.types ? portEl.dataset.types.split(',') : null,
        isInternalRouting: isInternalRouting
    };
    
    // Initialize mouse position for consistency
    const pos = getPointerPosFunc(e);
    lastMousePosition = { clientX: pos.clientX, clientY: pos.clientY };
    lastHoveredTargetPortInfo = null;

    portEl.classList.add('dragging-source');
    
    // Highlight valid targets
    const sourceConnector = portEl.dataset.connector || null;
    highlightTargetPorts(type, direction, sourceConnector, portEl);

    const tempCable = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    tempCable.id = 'temp-cable';
    tempCable.className.baseVal = 'cable-temp';
    
    // For internal routing, append to device's internal SVG layer so it renders on top
    if (isInternalRouting) {
        const deviceEl = document.getElementById(deviceId);
        const internalSvg = deviceEl?.querySelector('.device-internal-cables');
        if (internalSvg) {
            internalSvg.appendChild(tempCable);
        } else {
            cablesSvg.appendChild(tempCable);
        }
    } else {
        cablesSvg.appendChild(tempCable);
    }
}

/**
 * Find nearest target port within hit radius
 * For internal routing (sourceDirection='input'), finds output ports on same device
 * For normal routing, finds input/insert ports on any device
 */
export function findNearestInputPort(screenX, screenY, sourceType, sourceDirection = 'output') {
    const hitRadius = 24; // pixels - larger than visual port size for easier targeting
    let nearest = null;
    let nearestDist = hitRadius;
    
    const isInternalRouting = cableDragState?.isInternalRouting;
    const sourceDeviceId = cableDragState?.sourceDeviceId;
    
    // For internal routing, target output ports on same device
    // For normal routing, target input/insert ports on any device
    const targetSelector = isInternalRouting
        ? '.port[data-direction="output"]'
        : '.port[data-direction="input"], .port[data-direction="insert"]';
    
    document.querySelectorAll(targetSelector).forEach(port => {
        const targetDirection = port.dataset.direction;
        
        // For internal routing, only allow same device
        if (isInternalRouting && port.dataset.device !== sourceDeviceId) return;
        
        // For internal routing, use more permissive type matching
        // For normal routing, use standard type compatibility
        const typesCompatible = isInternalRouting
            ? canInternallyRoute(sourceType, port.dataset.type)
            : canConnectTypes(sourceType, port.dataset.type, sourceDirection, targetDirection);
        
        if (!typesCompatible) return;
        
        // Skip if it's the same port we're dragging from (for insert-to-insert on same device)
        if (cableDragState && cableDragState.sourceEl === port) return;
        
        const connector = port.querySelector('.port-connector');
        if (!connector) return;
        
        const rect = connector.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const dist = Math.hypot(screenX - centerX, screenY - centerY);
        
        if (dist < nearestDist) {
            nearestDist = dist;
            nearest = port;
        }
    });
    
    return nearest;
}

/**
 * Update temp cable position during drag
 */
export function updateTempCable(e, cachedTargetPortInfo = undefined) {
    const tempCable = document.getElementById('temp-cable');
    if (!tempCable || !cableDragState) return;

    const pos = getPointerPosFunc(e);
    
    const sourcePortPos = getPortPositionFunc(
        cableDragState.sourceDeviceId,
        cableDragState.sourceGroupIndex,
        cableDragState.sourceDirection,
        cableDragState.sourceIndex
    );
    
    if (!sourcePortPos) return;
    
    const endCanvasPos = screenToCanvasFunc(pos.clientX, pos.clientY);

    // Determine target port - either from cache (Alt key refresh) or fresh lookup
    let targetPort = null;
    if (cachedTargetPortInfo !== undefined) {
        // Using cached info - look up DOM element from stored info
        if (cachedTargetPortInfo !== null) {
            targetPort = document.querySelector(
                `.port[data-device="${cachedTargetPortInfo.deviceId}"][data-group="${cachedTargetPortInfo.groupIndex}"][data-direction="input"][data-index="${cachedTargetPortInfo.index}"]`
            );
            if (!targetPort) {
                targetPort = null;
            }
        }
    } else {
        // Fresh lookup via extended hit area
        targetPort = findNearestInputPort(pos.clientX, pos.clientY, cableDragState.sourceType);
        // Cache the target port INFO for Alt key refresh
        if (targetPort) {
            lastHoveredTargetPortInfo = {
                deviceId: targetPort.dataset.device,
                groupIndex: parseInt(targetPort.dataset.group),
                type: targetPort.dataset.type,
                index: parseInt(targetPort.dataset.index)
            };
        } else {
            lastHoveredTargetPortInfo = null;
        }
    }
    
    // Check type compatibility - use internal routing rules if applicable
    const isInternalRouting = cableDragState.isInternalRouting;
    const typesCompatible = targetPort && (
        isInternalRouting
            ? canInternallyRoute(cableDragState.sourceType, targetPort.dataset.type)
            : canConnectTypes(cableDragState.sourceType, targetPort.dataset.type)
    );
    
    if (targetPort && !typesCompatible) {
        tempCable.classList.add('cable-invalid');
    } else {
        tempCable.classList.remove('cable-invalid');
    }
    
    // Remove old preview cables
    document.querySelectorAll('.cable-multi-preview').forEach(p => p.remove());
    
    // Multi-drag preview handling
    if (cableDragState.isMultiDrag) {
        const multiPorts = cableDragState.multiPorts;
        const validTarget = typesCompatible;
        const flipSide = { left: 'right', right: 'left', top: 'bottom', bottom: 'top' };
        
        // For internal routing, get device position to convert to relative coordinates
        let deviceOffset = { x: 0, y: 0 };
        if (isInternalRouting) {
            const device = instanceModel.getById(cableDragState.sourceDeviceId);
            if (device) {
                deviceOffset = { x: device.position.x, y: device.position.y };
            }
        }
        
        // For internal routing, target direction is 'output'; for normal routing, 'input'
        const targetDirection = isInternalRouting ? 'output' : 'input';
        
        // Get the SVG container for preview cables (internal vs external)
        const previewContainer = isInternalRouting
            ? (document.getElementById(cableDragState.sourceDeviceId)?.querySelector('.device-internal-cables') || cablesSvg)
            : cablesSvg;
        
        if (validTarget) {
            // TARGETED MODE: ALL lines snap to actual routing positions
            const targetDeviceId = targetPort.dataset.device;
            const targetGroupIndex = parseInt(targetPort.dataset.group);
            const startTargetIndex = parseInt(targetPort.dataset.index);
            const useCompact = e.altKey;
            
            // Get target group max port count
            const targetDevice = instanceModel.getById(targetDeviceId);
            const targetTemplate = targetDevice ? getTemplateForDeviceFunc(targetDevice) : null;
            const targetGroup = targetTemplate?.portGroups[targetGroupIndex];
            const maxTargetIndex = targetGroup ? targetGroup.count : 999;
            
            // Calculate pattern offsets
            const firstSourceIndex = multiPorts[0].index;
            
            // Draw ALL preview cables snapped to actual targets
            multiPorts.forEach((port, i) => {
                let targetIndex;
                if (useCompact) {
                    targetIndex = startTargetIndex + i;
                } else {
                    const relativeOffset = port.index - firstSourceIndex;
                    targetIndex = startTargetIndex + relativeOffset;
                }
                
                if (targetIndex > maxTargetIndex || targetIndex < 1) return;
                
                const srcPos = getPortPositionFunc(port.deviceId, port.groupIndex, cableDragState.sourceDirection, port.index);
                const tgtPos = getPortPositionFunc(targetDeviceId, targetGroupIndex, targetDirection, targetIndex);
                
                if (srcPos && tgtPos) {
                    // For internal routing, use device-relative coordinates and flip sides
                    let srcX = srcPos.x - deviceOffset.x;
                    let srcY = srcPos.y - deviceOffset.y;
                    let tgtX = tgtPos.x - deviceOffset.x;
                    let tgtY = tgtPos.y - deviceOffset.y;
                    let srcSide = isInternalRouting ? (flipSide[srcPos.side] || srcPos.side) : srcPos.side;
                    let tgtSide = isInternalRouting ? (flipSide[tgtPos.side] || tgtPos.side) : tgtPos.side;
                    
                    if (i === 0) {
                        tempCable.setAttribute('d', createCablePathFunc(srcX, srcY, tgtX, tgtY, srcSide, tgtSide));
                    } else {
                        const previewCable = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                        previewCable.className.baseVal = 'cable-temp cable-multi-preview';
                        previewCable.setAttribute('d', createCablePathFunc(srcX, srcY, tgtX, tgtY, srcSide, tgtSide));
                        previewContainer.appendChild(previewCable);
                    }
                }
            });
        } else {
            // FREE MODE: First line follows cursor, others fan out
            const fanSpacing = 20;
            const useCompact = e.altKey;
            const firstSourceIndex = multiPorts[0].index;
            
            multiPorts.forEach((port, i) => {
                const srcPos = getPortPositionFunc(port.deviceId, port.groupIndex, cableDragState.sourceDirection, port.index);
                if (!srcPos) return;
                
                let fanOffset;
                if (useCompact) {
                    fanOffset = i * fanSpacing;
                } else {
                    const relativeOffset = port.index - firstSourceIndex;
                    fanOffset = relativeOffset * fanSpacing;
                }
                
                // For internal routing, use device-relative coordinates and flip sides
                const srcX = srcPos.x - deviceOffset.x;
                const srcY = srcPos.y - deviceOffset.y;
                const fanEndX = endCanvasPos.x - deviceOffset.x;
                const fanEndY = endCanvasPos.y - deviceOffset.y + fanOffset;
                const srcSide = isInternalRouting ? (flipSide[srcPos.side] || srcPos.side) : srcPos.side;
                const freeEndSide = flipSide[srcSide] || srcSide;
                
                if (i === 0) {
                    tempCable.setAttribute('d', createCablePathFunc(srcX, srcY, fanEndX, fanEndY, srcSide, freeEndSide));
                } else {
                    const previewCable = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                    previewCable.className.baseVal = 'cable-temp cable-multi-preview';
                    previewCable.setAttribute('d', createCablePathFunc(srcX, srcY, fanEndX, fanEndY, srcSide, freeEndSide));
                    previewContainer.appendChild(previewCable);
                }
            });
        }
    } else {
        // Single cable drag - reuse the typesCompatible check from above
        const flipSide = { left: 'right', right: 'left', top: 'bottom', bottom: 'top' };
        
        // For internal routing, get device position to convert to relative coordinates
        let deviceOffset = { x: 0, y: 0 };
        if (isInternalRouting) {
            const device = instanceModel.getById(cableDragState.sourceDeviceId);
            if (device) {
                deviceOffset = { x: device.position.x, y: device.position.y };
            }
        }
        
        if (typesCompatible) {
            // For internal routing, target is an output port; for normal routing, target is input
            const targetDir = isInternalRouting ? 'output' : 'input';
            const targetPortPos = getPortPositionFunc(
                targetPort.dataset.device,
                parseInt(targetPort.dataset.group),
                targetDir,
                parseInt(targetPort.dataset.index)
            );
            if (targetPortPos) {
                // For internal routing, flip sides so cable curves inward
                let srcSide = sourcePortPos.side;
                let tgtSide = targetPortPos.side;
                if (isInternalRouting) {
                    srcSide = flipSide[sourcePortPos.side] || sourcePortPos.side;
                    tgtSide = flipSide[targetPortPos.side] || targetPortPos.side;
                }
                // For internal routing, use device-relative coordinates
                const srcX = sourcePortPos.x - deviceOffset.x;
                const srcY = sourcePortPos.y - deviceOffset.y;
                const tgtX = targetPortPos.x - deviceOffset.x;
                const tgtY = targetPortPos.y - deviceOffset.y;
                const path = createCablePathFunc(srcX, srcY, tgtX, tgtY, srcSide, tgtSide);
                tempCable.setAttribute('d', path);
            } else {
                // Free mode fallback - flip for internal routing
                let srcSide = sourcePortPos.side;
                if (isInternalRouting) {
                    srcSide = flipSide[sourcePortPos.side] || sourcePortPos.side;
                }
                const freeEndSide = flipSide[srcSide] || srcSide;
                // For internal routing, use device-relative coordinates
                const srcX = sourcePortPos.x - deviceOffset.x;
                const srcY = sourcePortPos.y - deviceOffset.y;
                const endX = endCanvasPos.x - deviceOffset.x;
                const endY = endCanvasPos.y - deviceOffset.y;
                const path = createCablePathFunc(srcX, srcY, endX, endY, srcSide, freeEndSide);
                tempCable.setAttribute('d', path);
            }
        } else {
            // Free mode - flip source side for internal routing so cable curves inward
            let srcSide = sourcePortPos.side;
            if (isInternalRouting) {
                srcSide = flipSide[sourcePortPos.side] || sourcePortPos.side;
            }
            const freeEndSide = flipSide[srcSide] || srcSide;
            // For internal routing, use device-relative coordinates
            const srcX = sourcePortPos.x - deviceOffset.x;
            const srcY = sourcePortPos.y - deviceOffset.y;
            const endX = endCanvasPos.x - deviceOffset.x;
            const endY = endCanvasPos.y - deviceOffset.y;
            const path = createCablePathFunc(srcX, srcY, endX, endY, srcSide, freeEndSide);
            tempCable.setAttribute('d', path);
        }
    }
}

/**
 * Finish cable drag and create connection(s)
 */
export function finishCableDrag(e) {
    document.getElementById('temp-cable')?.remove();
    document.querySelectorAll('.cable-multi-preview').forEach(p => p.remove());
    clearPortHighlighting();
    
    lastHoveredTargetPortInfo = null;

    const pos = getPointerPosFunc(e);
    const useCompact = e.altKey;
    
    const sourceDirection = cableDragState?.sourceDirection || 'output';
    const targetPort = cableDragState ? findNearestInputPort(pos.clientX, pos.clientY, cableDragState.sourceType, sourceDirection) : null;

    if (targetPort && cableDragState) {
        const targetDeviceId = targetPort.dataset.device;
        const targetGroupIndex = parseInt(targetPort.dataset.group);
        const targetType = targetPort.dataset.type;
        const targetIndex = parseInt(targetPort.dataset.index);
        const targetDirection = targetPort.dataset.direction;
        const targetConnector = targetPort.dataset.connector || null;
        const targetTypes = targetPort.dataset.types ? targetPort.dataset.types.split(',') : null;

        const sourceTypes = cableDragState.sourceTypes || cableDragState.sourceType;
        const targetSignalTypes = targetTypes || targetType;
        const connectorOptions = {
            sourceConnector: cableDragState.sourceConnector,
            targetConnector: targetConnector
        };
        
        // For internal routing, use more permissive type matching
        const isInternalRouting = cableDragState.isInternalRouting;
        const typesCompatible = isInternalRouting
            ? canInternallyRoute(sourceTypes, targetSignalTypes)
            : canConnectTypes(sourceTypes, targetSignalTypes, sourceDirection, targetDirection, connectorOptions);

        if (typesCompatible) {
            if (cableDragState.isMultiDrag) {
                finishMultiCableDrag(targetDeviceId, targetGroupIndex, targetType, targetIndex, useCompact, sourceDirection, targetDirection);
            } else {
                // Single port routing
                const exists = connectionModel.some(c => {
                    const srcGroup = getConnectionSourceGroupFunc(c);
                    const tgtGroup = getConnectionTargetGroupFunc(c);
                    return c.sourceDeviceId === cableDragState.sourceDeviceId &&
                        srcGroup === cableDragState.sourceGroupIndex &&
                        c.sourcePortIndex === cableDragState.sourceIndex &&
                        c.targetDeviceId === targetDeviceId &&
                        tgtGroup === targetGroupIndex &&
                        c.targetPortIndex === targetIndex;
                });

                if (!exists) {
                    const result = createConnectionFunc(
                        cableDragState.sourceDeviceId,
                        cableDragState.sourceGroupIndex,
                        cableDragState.sourceType,
                        cableDragState.sourceIndex,
                        targetDeviceId,
                        targetGroupIndex,
                        targetType,
                        targetIndex,
                        { sourceDirection, targetDirection, isInternalRouting }
                    );
                    if (!result.success) {
                        showToast(result.error, true);
                    }
                } else {
                    showToast('Connection already exists', true);
                }
            }
        } else {
            const connectorOk = canConnectConnectors(cableDragState.sourceConnector, targetConnector);
            if (!connectorOk) {
                const srcName = CONNECTOR_TYPES[cableDragState.sourceConnector]?.label || cableDragState.sourceConnector;
                const tgtName = CONNECTOR_TYPES[targetConnector]?.label || targetConnector;
                showToast(`Incompatible connectors: ${srcName} ↔ ${tgtName}`, true);
            } else {
                showToast('Cannot connect incompatible signal types', true);
            }
        }
    }

    cableDragState = null;
}

/**
 * Finish multi-cable drag and create connections
 */
function finishMultiCableDrag(targetDeviceId, targetGroupIndex, targetType, startTargetIndex, useCompact = false, sourceDirection = 'output', targetDirection = 'input') {
    const multiPorts = cableDragState.multiPorts;
    
    const targetDevice = instanceModel.getById(targetDeviceId);
    if (!targetDevice) {
        showToast('Target device not found', true);
        clearMultiSelection();
        return;
    }
    
    const targetTemplate = getTemplateForDeviceFunc(targetDevice);
    if (!targetTemplate) {
        showToast('Target template not found', true);
        clearMultiSelection();
        return;
    }
    
    const targetGroup = targetTemplate.portGroups[targetGroupIndex];
    if (!targetGroup) {
        showToast('Target group not found', true);
        clearMultiSelection();
        return;
    }
    
    const maxTargetIndex = targetGroup.count;
    let createdCount = 0;
    let skippedCount = 0;
    
    const firstSourceIndex = multiPorts[0].index;
    
    multiPorts.forEach((port, offset) => {
        let targetIndex;
        
        if (useCompact) {
            targetIndex = startTargetIndex + offset;
        } else {
            const relativeOffset = port.index - firstSourceIndex;
            targetIndex = startTargetIndex + relativeOffset;
        }
        
        if (targetIndex > maxTargetIndex || targetIndex < 1) {
            skippedCount++;
            return;
        }
        
        const exists = connectionModel.some(c => {
            const srcGroup = getConnectionSourceGroupFunc(c);
            const tgtGroup = getConnectionTargetGroupFunc(c);
            return c.sourceDeviceId === port.deviceId &&
                srcGroup === port.groupIndex &&
                c.sourcePortIndex === port.index &&
                c.targetDeviceId === targetDeviceId &&
                tgtGroup === targetGroupIndex &&
                c.targetPortIndex === targetIndex;
        });
        
        if (!exists) {
            // Check if this is internal routing (same device)
            const isInternalRouting = port.deviceId === targetDeviceId;
            const result = createConnectionFunc(
                port.deviceId,
                port.groupIndex,
                port.type,
                port.index,
                targetDeviceId,
                targetGroupIndex,
                targetType,
                targetIndex,
                { sourceDirection, targetDirection, isInternalRouting }
            );
            if (result.success) {
                createdCount++;
            } else {
                skippedCount++;
            }
        }
    });
    
    clearMultiSelection();
    
    if (createdCount > 0) {
        let msg = `Routed ${createdCount} connection${createdCount > 1 ? 's' : ''}`;
        if (skippedCount > 0) {
            msg += ` (${skippedCount} skipped)`;
        }
        showToast(msg);
    } else if (skippedCount > 0) {
        showToast(`No connections created (blocked by fan rules or overflow)`, true);
    } else {
        showToast('Connections already exist', true);
    }
}
